import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Pencil, Plus, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Body } from "@/components/admin/AdminShell";
import { Card, Spinner } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { label } from "@/lib/admin";

const VARS = ["first_name", "resume_score", "assessment_summary", "verdict", "top_fix", "fix_list", "rewrite_block", "upgrade_url", "pricing_url", "pro_url", "elite_url", "pro_year_url", "elite_year_url", "plan_name", "unsubscribe_url"];

export const PreviewDialog = ({ t, onClose }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!t) return;
    api.post(`/admin/mk/templates/${enc(t.template_id)}/preview`).then((r) => setP(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const testSend = async () => { try { const r = await api.post(`/admin/mk/templates/${enc(t.template_id)}/test-send`); toast.success(`Test sent to ${r.data?.to || "you"}`); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] sm:max-w-6xl overflow-auto border-[#27272A] bg-[#121214]" data-testid="mk-preview-dialog">
        <DialogHeader><DialogTitle className="font-display">{p?.subject || t?.subject}</DialogTitle></DialogHeader>
        {!p ? <Spinner className="text-[#D4AF37]" /> : (
          <>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={testSend} data-testid="mk-test-send"><Send className="h-3.5 w-3.5" />Send me a test</Button>
              {p.url && <a href={p.url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid="mk-preview-open">Open full page</Button></a>}
            </div>
            <div className="grid gap-4 lg:grid-cols-[1fr_390px]">
              <div><div className="mb-1 text-[11px] uppercase text-zinc-500">Desktop</div><iframe title="Desktop preview" sandbox="" srcDoc={p.html} className="h-[560px] w-full rounded-lg bg-white" data-testid="mk-preview-desktop" /></div>
              <div><div className="mb-1 text-[11px] uppercase text-zinc-500">Mobile · 390px</div><iframe title="Mobile preview" sandbox="" srcDoc={p.html} className="h-[560px] w-[390px] max-w-full rounded-lg bg-white" data-testid="mk-preview-mobile" /></div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Editor = ({ t, onClose, onSaved }) => {
  const [f, setF] = useState(() => ({ name: t?.name || "", subject: t?.subject || "", body: t?.body || "<p>Hi {{first_name}},</p>\n<p></p>", category: t?.category || "" }));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { t?.template_id ? await api.put(`/admin/mk/templates/${enc(t.template_id)}`, f) : await api.post("/admin/mk/templates", f); toast.success("Template saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl border-[#27272A] bg-[#121214]" data-testid="mk-template-editor">
        <DialogHeader><DialogTitle className="font-display">{t?.template_id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" data-testid="mk-template-name" />
          <Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Category (optional)" data-testid="mk-template-category" />
        </div>
        <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" data-testid="mk-template-subject" />
        <Textarea rows={12} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="font-mono text-xs" data-testid="mk-template-body" />
        <div className="flex flex-wrap gap-1">{VARS.map((v) => <button key={v} onClick={() => setF({ ...f, body: `${f.body}{{${v}}}` })} className="rounded border border-[#27272A] px-1.5 py-0.5 font-mono text-[10px] text-zinc-400 hover:border-[#D4AF37]/50" data-testid={`mk-var-${v}`}>{`{{${v}}}`}</button>)}</div>
        <p className="text-[11px] text-zinc-500">Simple HTML. The header, sign-off, score note and unsubscribe link are added automatically. Every link is click-tracked.</p>
        <Button onClick={save} disabled={busy || !f.name.trim() || !f.subject.trim() || !f.body.trim()} data-testid="mk-template-save">{busy ? <Spinner /> : "Save template"}</Button>
      </DialogContent>
    </Dialog>
  );
};

export const Templates = ({ q }) => {
  const [edit, setEdit] = useState(null);
  const [prev, setPrev] = useState(null);
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/admin/mk/templates/${enc(t.template_id)}`); toast.success("Template deleted"); q.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <div className="mb-4 flex justify-end"><Button onClick={() => setEdit({})} data-testid="mk-template-new"><Plus className="h-4 w-4" />New template</Button></div>
      <Body q={q}>{(d) => (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {(d.items || []).map((t) => (
            <Card key={t.template_id} className="flex flex-col" data-testid={`mk-template-${t.template_id}`}>
              <div className="flex items-start justify-between gap-2"><div className="font-medium text-zinc-100">{t.name}</div>{t.builtin && <span className="rounded-full border border-[#D4AF37]/40 px-2 text-[9px] uppercase text-[#F3C649]">Built-in</span>}</div>
              <div className="mt-1 flex-1 text-sm text-zinc-400">{t.subject}</div>
              {t.category && <div className="mt-2 text-[10px] uppercase tracking-wider text-zinc-500">{label(t.category)}</div>}
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setPrev(t)} data-testid={`mk-template-preview-${t.template_id}`}><Eye className="h-3.5 w-3.5" />Preview</Button>
                <Button size="sm" variant="outline" onClick={() => setEdit(t)} data-testid={`mk-template-edit-${t.template_id}`}><Pencil className="h-3.5 w-3.5" />Edit</Button>
                {!t.builtin && <Button size="sm" variant="ghost" onClick={() => del(t)} className="text-red-400" data-testid={`mk-template-delete-${t.template_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}</Body>
      {edit && <Editor t={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); q.reload(); }} />}
      <PreviewDialog key={prev?.template_id} t={prev} onClose={() => setPrev(null)} />
    </>
  );
};
